import { render } from "preact";
import { html } from "htm/preact";
import { useEffect, useRef, useCallback } from "preact/hooks";
import {
  useSignal,
  useComputed,
  useSignalEffect,
  batch,
} from "@preact/signals";

/**
 * @typedef {{ x: number, y: number }} Point
 * @typedef {{ width: number, height: number }} CanvasSize
 * @typedef {{ zoom: number, offsetX: number, offsetY: number }} CropSettings
 * @typedef {"left" | "center" | "right"} TextAlign
 * @typedef {"regular" | "meme"} FontStyle
 * @typedef {"move" | "scale" | "rotate" | null} InteractionMode
 *
 * @typedef {{
 *   id: string,
 *   type: "image",
 *   name: string,
 *   image: HTMLImageElement,
 *   dataUrl: string,
 *   width: number,
 *   height: number,
 *   x: number,
 *   y: number,
 *   scale: number,
 *   rotation: number,
 *   opacity: number,
 *   crop: CropSettings,
 * }} ImageElement
 *
 * @typedef {{
 *   id: string,
 *   type: "text",
 *   name: string,
 *   text: string,
 *   fontFamily: FontStyle,
 *   fontSize: number,
 *   lineHeight: number,
 *   color: string,
 *   strokeColor: string,
 *   strokeWidth: number,
 *   textAlign: TextAlign,
 *   x: number,
 *   y: number,
 *   rotation: number,
 *   opacity: number,
 * }} TextElement
 *
 * @typedef {ImageElement | TextElement} EditorElement
 * @typedef {Partial<ImageElement> | Partial<TextElement>} ElementUpdate
 * @typedef {import("@preact/signals").Signal<EditorElement[]>} ElementsSignal
 * @typedef {import("@preact/signals").Signal<string | null>} ActiveElementIdSignal
 * @typedef {import("@preact/signals").Signal<CanvasSize>} CanvasSizeSignal
 * @typedef {import("@preact/signals").Signal<string>} StringSignal
 * @typedef {import("@preact/signals").Signal<HistorySnapshot[]>} HistorySignal
 * @typedef {import("preact").RefObject<HTMLCanvasElement | null>} CanvasRef
 * @typedef {import("preact").RefObject<HTMLInputElement | null>} InputRef
 * @typedef {{ elements: EditorElement[], activeElementId: string | null }} HistorySnapshot
 * @typedef {{ x: number, y: number, rect: DOMRect }} CanvasPointer
 * @typedef {{ width: number, height: number }} MeasuredBounds
 * @typedef {{ element: EditorElement | null, mode: InteractionMode }} HitResult
 * @typedef {(id: string) => void} SelectElementHandler
 * @typedef {(id: string, updates: ElementUpdate) => void} UpdateElementHandler
 * @typedef {(id: string) => void} ElementIdHandler
 * @typedef {(id: string, direction: "up" | "down") => void} MoveLayerHandler
 * @typedef {() => void} VoidHandler
 * @typedef {(event: Event) => void | Promise<void>} EventHandler
 * @typedef {{ canvasRef: CanvasRef, canvasSize: CanvasSize, activeElement: EditorElement | null, onExport: VoidHandler, onCopyToClipboard: VoidHandler, onSave: VoidHandler, onLoad: VoidHandler, onClear: VoidHandler, onUndo: VoidHandler, onRedo: VoidHandler, canUndo: boolean, canRedo: boolean }} CanvasPanelProps
 * @typedef {{ elements: EditorElement[], activeElementId: string | null, onSelectElement: SelectElementHandler, onMoveLayer: MoveLayerHandler, onRemoveElement: ElementIdHandler }} LayersPanelProps
 * @typedef {{ element: EditorElement, isActive: boolean, onSelect: VoidHandler, onMoveUp: VoidHandler, onMoveDown: VoidHandler, onDelete: VoidHandler }} LayerItemProps
 * @typedef {{ onAddImages: VoidHandler, onAddText: VoidHandler, onAddMemeText: VoidHandler, imagesInputRef: InputRef, onFilesChange: EventHandler }} AddContentPanelProps
 * @typedef {{ canvasSize: CanvasSize, backgroundColor: string, onCanvasSizeChange: (size: CanvasSize) => void, onBackgroundColorChange: (color: string) => void }} CanvasSettingsPanelProps
 * @typedef {{ activeElement: EditorElement | null, onUpdateElement: UpdateElementHandler, onDuplicate: VoidHandler, onDelete: VoidHandler, onBringForward: VoidHandler, onSendBackward: VoidHandler }} ActiveLayerPanelProps
 * @typedef {{ element: ImageElement, onUpdateElement: UpdateElementHandler }} ImageControlsProps
 * @typedef {{ element: TextElement, onUpdateElement: UpdateElementHandler }} TextControlsProps
 */

/**
 * ============================================================================
 * IMAGE EDITOR V2 - COMPLETE ARCHITECTURE OVERVIEW
 * ============================================================================
 *
 * 1. CORE CONCEPTS & TECH STACK
 *    - Framework: Preact (via `htm` for no-build JSX).
 *    - State: `@preact/signals` for high-performance reactive state.
 *    - Rendering: HTML5 Canvas (2D Context) for the main graphics area.
 *    - Styling: Tailwind CSS (utility classes).
 *
 * 2. APPLICATION STRUCTURE (UI COMPONENTS)
 *    The app is divided into a grid layout with distinct functional areas:
 *
 *    [ App Root ]
 *      ├── [ CanvasPanel ] (Left/Center)
 *      │     ├── <canvas> (The drawing surface)
 *      │     └── Toolbar (Export, Save, Load, Undo/Redo)
 *      │
 *      ├── [ LayersPanel ] (Bottom Left)
 *      │     └── List of all elements (Z-index management)
 *      │
 *      └── [ Sidebar ] (Right Column)
 *            ├── [ AddContentPanel ] (Buttons to add Images/Text)
 *            ├── [ CanvasSettingsPanel ] (Resize canvas, Background color)
 *            └── [ ActiveLayerPanel ] (Context-aware properties)
 *                  ├── Image Controls (Scale, Opacity, Crop)
 *                  └── Text Controls (Font, Color, Stroke, Content)
 *
 * 3. DATA FLOW & STATE MANAGEMENT
 *    The application uses a "Signal-driven" architecture to avoid React VDOM overhead.
 *
 *    A. Global State (Signals):
 *       - `elements`: Array of all objects (images, text) on the canvas.
 *       - `activeElementId`: ID of the currently selected object.
 *       - `canvasSize` / `backgroundColor`: Global canvas settings.
 *
 *    B. The Rendering Loop (`useSignalEffect`):
 *       - We do NOT use React to render the canvas content.
 *       - Instead, `useSignalEffect` observes `elements.value`.
 *       - When any signal changes, this effect runs immediately, clearing the canvas
 *         and redrawing all elements via `drawElement()`.
 *       - This ensures 60fps performance even with many objects.
 *
 *    C. User Interaction (The "Peek" Pattern):
 *       - Mouse/Touch events are handled by `useCanvasInteraction`.
 *       - Inside event handlers (`pointermove`), we use `.peek()` to read state.
 *       - This reads the *current* value without subscribing the event listener to updates.
 *       - Flow: Input -> `.peek()` State -> Calculate Math -> Update Signal -> Canvas Redraws.
 *
 * 4. LOGIC FLOWS
 *    - Adding Content: User clicks "Add Text" -> New object added to `elements` array -> Canvas redraws.
 *    - Selection: Click on canvas -> `hitTest()` checks coordinates -> `activeElementId` updates -> UI Sidebar updates.
 *    - Manipulation Flow A (Single Pointer Rotation):
 *        1. `pointerdown`: User clicks rotation handle. `hitTest()` returns `{ mode: "rotate" }`.
 *           - `saveToHistory()` snapshots current `elements.peek()` to `undoStack`.
 *           - `pointerStateRef.current.interaction` is set (stores `startAngle`, `elementId`).
 *        2. `pointermove` (runs ~60fps):
 *           - `handlePointerMove` calls `elements.peek()` to get fresh state.
 *           - Calculates `delta = currentAngle - startAngle`.
 *           - Updates `elements.value` with a new array containing the rotated element.
 *           - `useSignalEffect` detects `elements.value` change -> calls `drawElement()` for all items.
 *        3. `pointerup`: `handlePointerEnd` clears `pointerStateRef`. Final state remains in `elements`.
 *
 *    - Manipulation Flow B (Multi-touch Pinch Zoom):
 *        1. `pointerdown` (x2): User places two fingers. `pointerStateRef.current.pointers` tracks both IDs.
 *        2. `pointermove`: `handlePointerMove` detects `pointers.size >= 2`.
 *           - Calls `handlePinchInteraction()`.
 *           - Calculates center point (pan), distance ratio (scale), and angle delta (rotation).
 *           - Updates `elements.value` with all 3 properties simultaneously.
 *           - `useSignalEffect` redraws the canvas.
 *        3. `pointerup`: One finger lifts. `pinchStart` state is reset.
 *
 *    - History: Actions (like drag start) call `saveToHistory()`. Snapshots `elements.peek()` are pushed to `undoStack`.
 *
 * 5. FILE STRUCTURE
 *    - Imports & Constants: Config, helper functions (math, clamping).
 *    - Drawing Logic: Pure functions (`drawElement`, `drawActiveOverlay`) that render to Context2D.
 *    - Interaction Hooks: `useCanvasInteraction` (complex pointer logic), `useHistory`.
 *    - Main Component (`App`): Wires up signals and layout.
 *    - Sub-Components: `CanvasPanel`, `LayersPanel`, `ActiveLayerPanel` (UI rendering).
 * ============================================================================
 */

// --- Utility Functions ---

/**
 * Generates a unique ID for new elements.
 * Uses crypto.randomUUID if available, otherwise falls back to a timestamp + random string.
 * This ensures every layer has a distinct key for React/Preact rendering and logic.
 * @returns {string} A unique identifier string.
 */
function createId() {
  if (crypto && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `id-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * Converts radians to degrees.
 * Used for displaying rotation values in the UI (0-360).
 * @param {number} rad - Angle in radians.
 * @returns {number} Angle in degrees.
 */
function radiansToDegrees(rad) {
  return rad * (180 / Math.PI);
}

/**
 * Converts degrees to radians.
 * Used for converting UI slider values to Canvas rotation.
 * @param {number} deg - Angle in degrees.
 * @returns {number} Angle in radians.
 */
function degreesToRadians(deg) {
  return deg * (Math.PI / 180);
}

/**
 * Clamps a number between a minimum and maximum value.
 * Essential for keeping UI values and element properties within safe bounds.
 * @param {number} value - The value to clamp.
 * @param {number} min - The minimum allowed value.
 * @param {number} max - The maximum allowed value.
 * @returns {number} The clamped value.
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

// Global configuration constants for limits and UI sizing
const CONFIG = {
  minScale: 0.2,
  maxScale: 7,
  minFontSize: 12,
  maxFontSize: 260,
  rotationStep: Math.PI / 180,
  handleScreenRadius: 18, // Hit area size for rotation/scale handles
  rotateHandleOffset: 56, // Distance of the rotation handle from the element center
};

const serviceWorkerUpdateState = {
  activePointerCount: 0,
  hasProjectContent: false,
  lastInteractionAt: Date.now(),
  refreshing: false,
};

function markAppInteraction() {
  serviceWorkerUpdateState.lastInteractionAt = Date.now();
}

/**
 * @param {number} activePointerCount
 * @returns {void}
 */
function setActivePointerCount(activePointerCount) {
  serviceWorkerUpdateState.activePointerCount = activePointerCount;
  markAppInteraction();
}

/**
 * @param {boolean} hasProjectContent
 * @returns {void}
 */
function setHasProjectContent(hasProjectContent) {
  serviceWorkerUpdateState.hasProjectContent = hasProjectContent;
  if (hasProjectContent) {
    markAppInteraction();
  }
}

// --- Drawing Utilities ---
// These functions are "pure" in the sense that they don't depend on component state directly.
// They take a CanvasRenderingContext2D and an element object, and draw it.

/**
 * Draws a single element (Image or Text) onto the canvas context.
 * Handles transformations (translate, rotate, scale) and specific rendering logic.
 *
 * @param {CanvasRenderingContext2D} ctx - The canvas 2D context.
 * @param {EditorElement} element - The element data to draw.
 */
function drawElement(ctx, element) {
  /**
   * CANVAS STATE MANAGEMENT: ctx.save()
   *
   * What it does:
   * - Pushes the current canvas rendering state onto a stack (like Array.push)
   * - Saves ALL state properties: transformations (translate/rotate/scale),
   *   styles (fillStyle, strokeStyle, globalAlpha, lineWidth, lineCap, etc.),
   *   clipping regions, and compositing operations
   *
   * Why we need it here:
   * - We're about to apply transformations (translate, rotate) that are cumulative
   * - Without save/restore, transformations would stack up across multiple elements
   * - Example: If element1 is at (100, 100) and element2 at (200, 200),
   *   without restore, element2 would render at (300, 300) due to cumulative translate
   *
   * The Save/Restore Pattern:
   * 1. save() - Snapshot current "clean" state
   * 2. Apply transformations for THIS element only
   * 3. Draw element in its own transformed coordinate space
   * 4. restore() - Pop back to clean state, ready for next element
   *
   * Think of it like:
   * - save() = "Remember where I am now"
   * - restore() = "Teleport back to where I was"
   *
   * CRITICAL UNDERSTANDING - What restore() does NOT do:
   * - restore() does NOT undo pixels already drawn on the canvas
   * - Once drawImage() or fillText() executes, those pixels are PERMANENT
   * - restore() only resets the SETTINGS (transform matrix, styles) for future draws
   * - The transformations we apply AFFECT the drawing when it happens,
   *   then restore() just resets the coordinate system for the next element
   */
  ctx.save();

  /**
   * WHY WE TRANSFORM THE CANVAS (Rotate the Paper) vs. CALCULATING POSITIONS
   *
   * Good question: "Why not just calculate the rotated position and draw there?"
   *
   * The Canvas API Constraint:
   * - ctx.drawImage(img, x, y, w, h) has NO rotation parameter
   * - ctx.fillText(text, x, y) has NO rotation parameter
   * - These methods can only draw axis-aligned (non-rotated) content
   *
   * Your options for rotation:
   * 1. Transform the coordinate system (what we do here) ✅ Simple!
   * 2. Manually calculate corner positions + use advanced matrix math ❌ Complex!
   * 3. Manipulate pixels directly ❌ Extremely slow!
   *
   * Why transforming is easier:
   * - We say: "Rotate the whole world, then draw at (0,0)"
   * - Browser handles all the rotation math internally (very fast, GPU-accelerated)
   * - Works identically for images, text, shapes, anything
   *
   * Analogy:
   * - Transforming canvas = Rotating the paper, painting straight
   * - Manual calculation = Keeping paper flat, tilting your hand at weird angles
   * The first approach is much more natural!
   */

  // Apply transformations: Move to position -> Rotate -> Apply Opacity
  // These affect WHERE and HOW the next draw call will render
  ctx.translate(element.x, element.y); // "Move the paper so origin is at element center"
  ctx.rotate(element.rotation); // "Rotate the paper by element's rotation"
  ctx.globalAlpha = element.opacity ?? 1; // "Set brush opacity"

  if (element.type === "image") {
    // Scale is applied here for images
    ctx.scale(element.scale, element.scale);

    const { image, width, height, crop } = element;
    const zoom = crop.zoom;

    // Calculate source rectangle (sx, sy, sWidth, sHeight) for cropping
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    const cropWidth = naturalWidth / zoom;
    const cropHeight = naturalHeight / zoom;

    // Calculate max offsets to keep crop within bounds
    const offsetMaxX = (naturalWidth - cropWidth) / 2;
    const offsetMaxY = (naturalHeight - cropHeight) / 2;

    // Map normalized offsets (-1 to 1) to pixel coordinates
    const sx = clamp(
      offsetMaxX * (crop.offsetX + 1),
      0,
      naturalWidth - cropWidth,
    );
    const sy = clamp(
      offsetMaxY * (crop.offsetY + 1),
      0,
      naturalHeight - cropHeight,
    );

    // Draw the cropped image centered at (0,0)
    ctx.drawImage(
      image,
      sx,
      sy,
      cropWidth,
      cropHeight,
      -width / 2, // Center horizontally
      -height / 2, // Center vertically
      width,
      height,
    );
  } else if (element.type === "text") {
    const fontSize = element.fontSize;
    // Choose font stack based on style
    const fontFamily =
      element.fontFamily === "meme"
        ? "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif"
        : "Arial, 'Helvetica Neue', Helvetica, sans-serif";

    ctx.font = `${
      element.fontFamily === "meme" ? "800" : "600"
    } ${fontSize}px ${fontFamily}`;
    ctx.textAlign = element.textAlign;
    ctx.textBaseline = "middle";

    // Handle multi-line text
    const lines = element.text.split("\n");
    const lineHeight = fontSize * element.lineHeight;
    const blockHeight = lineHeight * lines.length;

    lines.forEach((line, index) => {
      // Calculate Y position for each line to center the text block vertically
      const y = -blockHeight / 2 + index * lineHeight + lineHeight / 2;

      // Draw stroke (outline) if width > 0
      if (element.strokeWidth > 0) {
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.strokeStyle = element.strokeColor;
        ctx.lineWidth = element.strokeWidth;
        ctx.strokeText(line, 0, y);
      }

      // Draw fill
      ctx.fillStyle = element.color;
      ctx.fillText(line, 0, y);
      // ☝️ AT THIS MOMENT: Pixels are written to canvas with current transformations
      // Those pixels are now PERMANENT (until we clearRect the whole canvas)
    });
  }

  /**
   * CANVAS STATE MANAGEMENT: ctx.restore()
   *
   * What restore() DOES do:
   * - Pops the saved state from the stack (undoes save())
   * - Resets coordinate system: origin back to (0,0), rotation back to 0
   * - Resets all styles: globalAlpha back to 1.0, etc.
   * - Prepares a clean slate for the next element to draw
   *
   * What restore() does NOT do:
   * - Does NOT erase or move the pixels we just drew (they stay exactly where they are!)
   * - Does NOT "undo" the drawImage() or fillText() calls above
   * - The image/text is already painted onto the canvas at the transformed position
   *
   * Mental Model:
   * Think of the canvas as a physical painting:
   * - save() = "Remember my brush position and settings"
   * - translate/rotate = "Move to a new position, tilt the canvas"
   * - drawImage/fillText = "Apply paint to canvas" (PERMANENT!)
   * - restore() = "Put the canvas back flat, reset my position" (paint stays!)
   *
   * Why this matters:
   * - The element is drawn ONCE at its transformed position
   * - restore() prevents the NEXT element from inheriting these transformations
   * - Without restore(), element2 would get element1's transformations ADDED to its own
   */
  ctx.restore();
  ctx.globalAlpha = 1; // Reset alpha just in case (belt-and-suspenders approach)
}

/**
 * Calculates the bounding box dimensions of an element.
 * Useful for hit testing and drawing selection borders.
 *
 * @param {CanvasRenderingContext2D} ctx - The canvas context (used for text measurement).
 * @param {EditorElement} element - The element to measure.
 * @returns {MeasuredBounds} An object containing { width, height }.
 */
function getElementDimensions(ctx, element) {
  if (element.type === "image") {
    // For images, dimensions are straightforward: width * scale
    return {
      width: element.width * element.scale,
      height: element.height * element.scale,
    };
  }

  // For text, we need to measure the widest line
  const fontSize = element.fontSize;
  const lines = element.text.split("\n");
  const fontFamily =
    element.fontFamily === "meme"
      ? "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif"
      : "Arial, 'Helvetica Neue', Helvetica, sans-serif";

  /**
   * CANVAS STATE MANAGEMENT: ctx.save()
   *
   * Why save/restore for text measurement:
   * - We need to temporarily set ctx.font to measure text width accurately
   * - ctx.font is a GLOBAL canvas property that persists across operations
   * - Without save/restore, changing font here would affect ALL subsequent canvas operations
   * - This function is called from hitTest, drawActiveOverlay, and render loop
   *
   * Preventing side effects:
   * - This is a "pure" utility function - it should only READ data, not modify state
   * - save/restore ensures we leave the canvas context exactly as we found it
   * - Other code can call this function without worrying about font corruption
   *
   * What we're protecting:
   * - ctx.font property (the only thing we modify before restore)
   * - If we didn't restore, the canvas might render OTHER text in the wrong font
   */
  ctx.save();
  ctx.font = `${
    element.fontFamily === "meme" ? "800" : "600"
  } ${fontSize}px ${fontFamily}`;

  let maxWidth = 0;
  lines.forEach((line) => {
    const metrics = ctx.measureText(line || " ");
    maxWidth = Math.max(maxWidth, metrics.width);
  });

  /**
   * CANVAS STATE MANAGEMENT: ctx.restore()
   *
   * Cleanup after measurement:
   * - Restores ctx.font to whatever it was before we entered this function
   * - Critical because this function is called FREQUENTLY during interactions:
   *   • Every pointermove event (hit testing during drag)
   *   • Every render cycle (when drawing selection overlay)
   *   • Multiple times per frame during multi-touch gestures
   *
   * Without this restore:
   * - If we measured a Meme text (Impact 800), canvas would stay in that font
   * - Next regular text would measure/render incorrectly
   * - Selection overlays might use wrong font for hit detection
   * - Creates subtle bugs where interaction breaks after touching certain elements
   */
  ctx.restore();

  const padding = 24; // Add some padding around text for better hit area
  const height = lines.length * fontSize * element.lineHeight;
  return { width: maxWidth + padding, height: height + padding };
}

/**
 * Draws the selection overlay (dashed box + handles) for the active element.
 * This provides visual feedback for selection and controls for rotation/scaling.
 *
 * @param {CanvasRenderingContext2D} ctx - The canvas context.
 * @param {EditorElement} element - The active element to draw the overlay for.
 */
function drawActiveOverlay(ctx, element) {
  /**
   * CANVAS STATE MANAGEMENT: ctx.save()
   *
   * Why we need save here:
   * - We're about to draw UI controls (selection box, rotation handle, scale handle)
   * - These controls must align PERFECTLY with the element's transformed position
   * - We apply the SAME transformations as the element (translate + rotate)
   * - This lets us draw in "element local space" where math is simpler
   *
   * Coordinate space transformation:
   * - After translate(element.x, element.y) + rotate(element.rotation):
   *   • Origin (0, 0) is now at the element's center
   *   • X-axis points along element's "right" direction (accounting for rotation)
   *   • Y-axis points along element's "down" direction (accounting for rotation)
   * - We can now draw handles at simple offsets like (width/2, height/2) for bottom-right
   *
   * Why this matters:
   * - Without matching the element's transformation, handles would be misaligned
   * - Example: Rotated 45°, handles would point wrong direction relative to element
   * - save/restore ensures this transformation only affects the overlay, not subsequent draws
   */
  ctx.save();
  // Move context to element center and rotate to match element
  ctx.translate(element.x, element.y);
  ctx.rotate(element.rotation);

  const dims = getElementDimensions(ctx, element);
  const { width, height } = dims;

  // Draw dashed border around the element
  ctx.strokeStyle = "rgba(148, 163, 184, 0.8)";
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 6]);
  ctx.strokeRect(-width / 2, -height / 2, width, height);
  ctx.setLineDash([]);

  // Draw resize handle (bottom-right corner)
  // Note: We draw it at width/2, height/2 which corresponds to the bottom-right
  // relative to the center point (0,0)
  const handleSize = 18;
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#1e293b";
  ctx.beginPath();
  ctx.arc(width / 2, height / 2, handleSize / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Draw rotation handle (sticking out from top)
  const rotateHandleY = -height / 2 - CONFIG.rotateHandleOffset;
  ctx.beginPath();
  ctx.moveTo(0, -height / 2); // Line from top of box
  ctx.lineTo(0, rotateHandleY + handleSize / 2); // To handle
  ctx.strokeStyle = "rgba(148, 163, 184, 0.7)";
  ctx.lineWidth = 2;
  ctx.stroke();

  // Draw rotation knob
  ctx.beginPath();
  ctx.arc(0, rotateHandleY, handleSize / 2, 0, Math.PI * 2);
  ctx.fillStyle = "#38bdf8"; // Light blue
  ctx.fill();
  ctx.strokeStyle = "#0f172a";
  ctx.stroke();

  /**
   * CANVAS STATE MANAGEMENT: ctx.restore()
   *
   * Returning to global coordinates:
   * - Undoes the translate(element.x, element.y) and rotate(element.rotation)
   * - Returns origin back to top-left corner of canvas
   * - Resets rotation back to 0 (no rotation)
   * - Also restores any style changes (strokeStyle, fillStyle, lineWidth, lineDash)
   *
   * Why this is essential:
   * - This function is called FROM WITHIN the main render loop (useSignalEffect)
   * - After drawing the overlay, we might draw OTHER elements or overlays
   * - Without restore, next element would inherit this element's transformation
   * - Would create cascading coordinate corruption across all subsequent draws
   *
   * Interaction with main render loop:
   * - Main loop: save() -> draw background -> draw all elements -> draw overlay -> restore()
   * - Each drawElement() and drawActiveOverlay() has its OWN save/restore pair
   * - Creates nested state stack: [main] -> [element] -> [overlay]
   * - Each restore() pops one level, maintaining clean state at each nesting level
   */
  ctx.restore();
}

/**
 * Deep clones the elements array for history snapshots.
 * Ensures that nested objects like `crop` are also cloned to prevent reference issues.
 * @param {EditorElement[]} els - The array of elements to clone.
 * @returns {EditorElement[]} A deep copy of the elements array.
 */
function cloneElements(els) {
  return els.map((el) => {
    // Shallow copy first
    if (el.type === "image") {
      return /** @type {ImageElement} */ ({
        ...el,
        crop: { ...el.crop },
      });
    }

    return /** @type {TextElement} */ ({ ...el });
  });
}

/**
 * Normalizes an angle to the range [-π, π].
 * This prevents rotation values from growing indefinitely and keeps them compatible with UI sliders.
 * @param {number} angle - The angle in radians.
 * @returns {number} The normalized angle in radians.
 */
function normalizeRotation(angle) {
  const twoPi = Math.PI * 2;
  // Ensure positive modulus
  let normalized = ((angle % twoPi) + twoPi) % twoPi;
  // Shift range from [0, 2π] to [-π, π] for easier UI handling
  if (normalized > Math.PI) {
    normalized -= twoPi;
  }
  return normalized;
}

/**
 * Maps a pointer event's client coordinates to the canvas internal coordinate system.
 * Handles CSS scaling of the canvas element correctly.
 * @param {PointerEvent} event - The pointer event.
 * @param {HTMLCanvasElement} canvas - The canvas element.
 * @returns {CanvasPointer} The {x, y} coordinates in canvas space and the bounding rect.
 */
function pointerToCanvas(event, canvas) {
  const rect = canvas.getBoundingClientRect();
  // Map client coordinates to canvas resolution
  const x = ((event.clientX - rect.left) / rect.width) * canvas.width;
  const y = ((event.clientY - rect.top) / rect.height) * canvas.height;
  return { x, y, rect };
}

/**
 * Transforms a point from an element's local space to global canvas space.
 * Applies rotation and translation.
 * @param {EditorElement} element - The element providing the coordinate system.
 * @param {number} localX - X coordinate in element's local space.
 * @param {number} localY - Y coordinate in element's local space.
 * @returns {Point} The {x, y} coordinates in global canvas space.
 */
function localToCanvas(element, localX, localY) {
  const cos = Math.cos(element.rotation);
  const sin = Math.sin(element.rotation);
  // Apply 2D rotation matrix and translation
  const canvasX = element.x + localX * cos - localY * sin;
  const canvasY = element.y + localX * sin + localY * cos;
  return { x: canvasX, y: canvasY };
}

/**
 * Maps a point from canvas internal coordinates back to client (screen) coordinates.
 * Useful for determining where to draw UI overlays or checking hit regions against screen events.
 * @param {DOMRect} rect - The bounding client rect of the canvas.
 * @param {Point} canvasPoint - The {x, y} point in canvas space.
 * @param {HTMLCanvasElement} canvas - The canvas element.
 * @returns {Point} The {x, y} coordinates in client space.
 */
function canvasToClient(rect, canvasPoint, canvas) {
  return {
    x: rect.left + (canvasPoint.x / canvas.width) * rect.width,
    y: rect.top + (canvasPoint.y / canvas.height) * rect.height,
  };
}

/**
 * Checks if a point (in canvas space) lies within the bounds of an element.
 * Accounts for the element's rotation by transforming the point to local space.
 * @param {EditorElement} element - The element to check against.
 * @param {CanvasPointer} pointer - The pointer coordinates {x, y} in canvas space.
 * @param {CanvasRenderingContext2D} ctx - Context for measuring text dimensions.
 * @returns {boolean} True if the point is inside the element.
 */
function pointInsideElement(element, pointer, ctx) {
  const dims = getElementDimensions(ctx, element);

  // Transform pointer to element's local coordinate system (inverse rotation)
  const dx = pointer.x - element.x;
  const dy = pointer.y - element.y;
  const cos = Math.cos(-element.rotation);
  const sin = Math.sin(-element.rotation);
  const localX = dx * cos - dy * sin;
  const localY = dx * sin + dy * cos;

  // Check if point is within the axis-aligned bounding box in local space
  return (
    localX >= -dims.width / 2 &&
    localX <= dims.width / 2 &&
    localY >= -dims.height / 2 &&
    localY <= dims.height / 2
  );
}

/**
 * Checks if the pointer is hovering over any control handles (scale, rotate) of an element.
 * Uses screen-space distance to ensure hit targets are easy to tap/click regardless of zoom.
 * @param {EditorElement} element - The element to check.
 * @param {CanvasPointer} pointer - Pointer info including canvas coordinates and rect.
 * @param {Point} client - Raw client coordinates {x, y} of the event.
 * @param {CanvasRenderingContext2D} ctx - Context for measuring dimensions.
 * @param {HTMLCanvasElement} canvas - The canvas element.
 * @returns {HitResult | null} The hit result { element, mode } or null.
 */
function detectHandles(element, pointer, client, ctx, canvas) {
  const dims = getElementDimensions(ctx, element);

  // Define handle positions in local space
  /** @type {{ mode: Exclude<InteractionMode, null>, position: Point }[]} */
  const handles = [
    {
      mode: "scale",
      position: localToCanvas(element, dims.width / 2, dims.height / 2),
    },
    {
      mode: "rotate",
      position: localToCanvas(
        element,
        0,
        -dims.height / 2 - CONFIG.rotateHandleOffset,
      ),
    },
  ];

  // Check distance from pointer to each handle
  for (const handle of handles) {
    const clientPoint = canvasToClient(pointer.rect, handle.position, canvas);
    const distance = Math.hypot(
      client.x - clientPoint.x,
      client.y - clientPoint.y,
    );
    if (distance <= CONFIG.handleScreenRadius) {
      return { element, mode: handle.mode };
    }
  }
  return null;
}

// --- Custom Hooks ---

/**
 * Manages the Undo/Redo history stack.
 *
 * @param {ElementsSignal} elements - The signal containing the array of canvas elements.
 * @param {ActiveElementIdSignal} activeElementId - The signal containing the ID of the currently selected element.
 * @returns {{ undo: VoidHandler, redo: VoidHandler, saveToHistory: VoidHandler, undoStack: HistorySignal, redoStack: HistorySignal }} - undo, redo, saveToHistory functions and the stack signals.
 */
function useHistory(elements, activeElementId) {
  // useSignal: Creates reactive state for the history stacks.
  // We use signals here so that the UI (Undo/Redo buttons) can subscribe to changes
  // without re-rendering the entire component tree unnecessarily.
  const undoStack = useSignal(/** @type {HistorySnapshot[]} */ ([]));
  const redoStack = useSignal(/** @type {HistorySnapshot[]} */ ([]));

  // useCallback: Memoizes the function to prevent it from being recreated on every render.
  // This is important because this function is passed down to child components.
  const saveToHistory = useCallback(() => {
    const snapshot = {
      elements: cloneElements(elements.peek()), // .peek() reads value without subscribing
      activeElementId: activeElementId.peek(),
    };

    // batch: Groups multiple signal updates into a single transaction.
    // This prevents multiple re-renders or effect triggers.
    batch(() => {
      undoStack.value = [...undoStack.value, snapshot].slice(-50); // Limit history to 50 steps
      redoStack.value = []; // Clear redo stack on new action
    });
  }, [elements, activeElementId, undoStack, redoStack]);

  const undo = useCallback(() => {
    if (undoStack.value.length === 0) return;

    // Save current state to redo stack before undoing
    const currentSnapshot = {
      elements: cloneElements(elements.peek()),
      activeElementId: activeElementId.peek(),
    };
    const previous = undoStack.value[undoStack.value.length - 1];

    batch(() => {
      redoStack.value = [...redoStack.value, currentSnapshot];
      undoStack.value = undoStack.value.slice(0, -1);

      // Restore elements from history
      const restoredElements = cloneElements(previous.elements);
      for (let i = 0, total = restoredElements.length; i < total; i++) {
        const element = restoredElements[i];
        if (element.type !== "image") {
          continue;
        }

        const img = new Image();
        img.src = element.dataUrl;
        element.image = img;
      }

      elements.value = restoredElements;
      // Restore selection if the element still exists
      activeElementId.value =
        previous.activeElementId &&
        restoredElements.find((e) => e.id === previous.activeElementId)
          ? previous.activeElementId
          : null;
    });
  }, [elements, activeElementId, undoStack, redoStack]);

  const redo = useCallback(() => {
    if (redoStack.value.length === 0) return;

    const currentSnapshot = {
      elements: cloneElements(elements.peek()),
      activeElementId: activeElementId.peek(),
    };
    const next = redoStack.value[redoStack.value.length - 1];

    batch(() => {
      undoStack.value = [...undoStack.value, currentSnapshot];
      redoStack.value = redoStack.value.slice(0, -1);

      const restoredElements = cloneElements(next.elements);
      for (let i = 0, total = restoredElements.length; i < total; i++) {
        const element = restoredElements[i];
        if (element.type !== "image") {
          continue;
        }

        const img = new Image();
        img.src = element.dataUrl;
        element.image = img;
      }

      elements.value = restoredElements;
      activeElementId.value =
        next.activeElementId &&
        restoredElements.find((e) => e.id === next.activeElementId)
          ? next.activeElementId
          : null;
    });
  }, [elements, activeElementId, undoStack, redoStack]);

  return { undo, redo, saveToHistory, undoStack, redoStack };
}

/**
 * Handles all canvas interactions: selection, moving, scaling, rotating, and pinch-to-zoom.
 *
 * @param {CanvasRef} canvasRef - Ref to the canvas DOM element.
 * @param {ElementsSignal} elements - Signal for the list of elements.
 * @param {ActiveElementIdSignal} activeElementId - Signal for the active element ID.
 * @param {VoidHandler} saveToHistory - Function to save state before modification.
 */
function useCanvasInteraction(
  canvasRef,
  elements,
  activeElementId,
  saveToHistory,
) {
  // useRef: Stores mutable state that doesn't trigger re-renders.
  // We use this for tracking pointer events (mouse/touch) because they fire very rapidly.
  // Triggering a React render on every 'pointermove' would be too slow.
  const pointerStateRef = useRef({
    pointers: new Map(), // Tracks active pointers (for multi-touch)
    interaction: null, // Stores current interaction mode (move, scale, rotate)
  });

  // useEffect: Sets up event listeners when the component mounts (or dependencies change).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Helper context for hit testing (measuring text width, etc.)
    const ctxRef = { current: canvas.getContext("2d") };

    // Main hit test function: checks handles first, then element bodies
    // Iterates in reverse order (top-most elements first)
    const hitTest = (event) => {
      const pointer = pointerToCanvas(event, canvas);
      const client = { x: event.clientX, y: event.clientY };

      // PERFORMANCE: Use .peek() to read state without subscribing.
      // This function is called on every click/touch, so we just need the current snapshot.
      const currentElements = elements.peek();

      for (let index = currentElements.length - 1; index >= 0; index -= 1) {
        const element = currentElements[index];

        // Check handles first (priority over moving)
        const handleHit = detectHandles(
          element,
          pointer,
          client,
          ctxRef.current,
          canvas,
        );
        if (handleHit) {
          return handleHit;
        }

        // Then check if clicking inside the element
        if (pointInsideElement(element, pointer, ctxRef.current)) {
          return { element, mode: "move" };
        }
      }
      return { element: null, mode: null };
    };

    // Event Handler: Pointer Down (Start interaction)
    const handlePointerDown = (event) => {
      canvas.setPointerCapture(event.pointerId); // Capture pointer to track it even if it leaves canvas
      const { element, mode } = hitTest(event);
      const state = pointerStateRef.current;
      state.pointers.set(event.pointerId, pointerToCanvas(event, canvas));
      setActivePointerCount(state.pointers.size);

      if (!element) {
        activeElementId.value = null; // Deselect if clicking empty space
        state.interaction = null;
        return;
      }

      // Select the element
      // PERFORMANCE: Check current value with .peek() to avoid redundant updates.
      if (activeElementId.peek() !== element.id) {
        activeElementId.value = element.id;
      }

      saveToHistory(); // Save state before starting drag/resize

      // Store initial state for the interaction
      state.interaction = {
        type: mode,
        elementId: element.id,
        pointerId: event.pointerId,
        startPointer: pointerToCanvas(event, canvas),
        startElement: {
          x: element.x,
          y: element.y,
          rotation: element.rotation,
          scale: element.type === "image" ? element.scale : 1,
          fontSize:
            element.type === "text" ? element.fontSize : CONFIG.minFontSize,
        },
      };
    };

    // Handles multi-touch pinch (zoom/rotate) gestures
    const handlePinchInteraction = (element) => {
      const state = pointerStateRef.current;
      const pointers = Array.from(state.pointers.values());
      if (pointers.length < 2 || !state.interaction) return;

      const [first, second] = pointers;
      const interaction = state.interaction;

      // Initialize pinch state if not already started
      if (!interaction.pinchStart) {
        const distance = Math.hypot(second.x - first.x, second.y - first.y);
        const angle = Math.atan2(second.y - first.y, second.x - first.x);
        interaction.pinchStart = {
          distance,
          angle,
          center: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
          element: { ...element },
        };
        return;
      }

      // Calculate changes
      const start = interaction.pinchStart;
      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      const angle = Math.atan2(second.y - first.y, second.x - first.x);
      const center = {
        x: (first.x + second.x) / 2,
        y: (first.y + second.y) / 2,
      };

      const updates = {};

      // Scale logic
      if (start.distance > 0) {
        const ratio = distance / start.distance;
        if (element.type === "image") {
          updates.scale = clamp(
            start.element.scale * ratio,
            CONFIG.minScale,
            CONFIG.maxScale,
          );
        } else if (element.type === "text") {
          updates.fontSize = clamp(
            start.element.fontSize * ratio,
            CONFIG.minFontSize,
            CONFIG.maxFontSize,
          );
        }
      }

      // Rotate logic
      updates.rotation = normalizeRotation(
        start.element.rotation + (angle - start.angle),
      );

      // Move logic (pan)
      updates.x = start.element.x + (center.x - start.center.x);
      updates.y = start.element.y + (center.y - start.center.y);

      // Update element signal
      // PERFORMANCE: We use .peek() here because we are inside a high-frequency event loop.
      // We need the current state to calculate the next frame, but we don't want this
      // specific function closure to subscribe to updates (which would be circular or wasteful).
      elements.value = elements
        .peek()
        .map((el) => (el.id === element.id ? { ...el, ...updates } : el));
    };

    // Event Handler: Pointer Move (Drag/Resize/Rotate)
    const handlePointerMove = (event) => {
      const state = pointerStateRef.current;
      if (!state.interaction) return;

      state.pointers.set(event.pointerId, pointerToCanvas(event, canvas));
      markAppInteraction();
      const interaction = state.interaction;

      // PERFORMANCE: .peek() is critical here.
      // This runs at 60fps. We need to read the array to find the element,
      // but we must NOT subscribe to it, or we'd trigger infinite loops or overhead.
      const currentElements = elements.peek();
      const element = currentElements.find(
        (item) => item.id === interaction.elementId,
      );
      if (!element) return;

      // If two fingers, switch to pinch mode
      if (state.pointers.size >= 2) {
        handlePinchInteraction(element);
        return;
      }

      const pointerInfo = pointerToCanvas(event, canvas);
      const updates = {};

      // Handle single-pointer interactions
      if (interaction.type === "move") {
        const dx = pointerInfo.x - interaction.startPointer.x;
        const dy = pointerInfo.y - interaction.startPointer.y;
        updates.x = interaction.startElement.x + dx;
        updates.y = interaction.startElement.y + dy;
      } else if (interaction.type === "scale" && element.type === "image") {
        const startDistance = Math.hypot(
          interaction.startPointer.x - interaction.startElement.x,
          interaction.startPointer.y - interaction.startElement.y,
        );
        const currentDistance = Math.hypot(
          pointerInfo.x - interaction.startElement.x,
          pointerInfo.y - interaction.startElement.y,
        );
        if (startDistance > 0) {
          const ratio = currentDistance / startDistance;
          updates.scale = clamp(
            interaction.startElement.scale * ratio,
            CONFIG.minScale,
            CONFIG.maxScale,
          );
        }
      } else if (interaction.type === "scale" && element.type === "text") {
        const startDistance = Math.hypot(
          interaction.startPointer.x - interaction.startElement.x,
          interaction.startPointer.y - interaction.startElement.y,
        );
        const currentDistance = Math.hypot(
          pointerInfo.x - interaction.startElement.x,
          pointerInfo.y - interaction.startElement.y,
        );
        if (startDistance > 0) {
          const ratio = currentDistance / startDistance;
          updates.fontSize = clamp(
            interaction.startElement.fontSize * ratio,
            CONFIG.minFontSize,
            CONFIG.maxFontSize,
          );
        }
      } else if (interaction.type === "rotate") {
        const startAngle = Math.atan2(
          interaction.startPointer.y - interaction.startElement.y,
          interaction.startPointer.x - interaction.startElement.x,
        );
        const currentAngle = Math.atan2(
          pointerInfo.y - interaction.startElement.y,
          pointerInfo.x - interaction.startElement.x,
        );
        const delta = currentAngle - startAngle;
        updates.rotation = normalizeRotation(
          interaction.startElement.rotation + delta,
        );
      }

      // Apply updates
      // PERFORMANCE: Again, using .peek() to read the source array for the update.
      // The assignment to .value will trigger the useSignalEffect to redraw the canvas.
      elements.value = elements
        .peek()
        .map((el) => (el.id === element.id ? { ...el, ...updates } : el));
    }; // Event Handler: Pointer Up (End interaction)
    const handlePointerEnd = (event) => {
      const state = pointerStateRef.current;
      state.pointers.delete(event.pointerId);
      setActivePointerCount(state.pointers.size);
      canvas.releasePointerCapture(event.pointerId);

      if (state.pointers.size < 2 && state.interaction) {
        state.interaction.pinchStart = null;
      }
      if (state.pointers.size === 0) {
        state.interaction = null;
      }
    };

    // Attach listeners
    canvas.addEventListener("pointerdown", handlePointerDown);
    canvas.addEventListener("pointermove", handlePointerMove);
    canvas.addEventListener("pointerup", handlePointerEnd);
    canvas.addEventListener("pointercancel", handlePointerEnd);

    // Cleanup listeners on unmount
    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown);
      canvas.removeEventListener("pointermove", handlePointerMove);
      canvas.removeEventListener("pointerup", handlePointerEnd);
      canvas.removeEventListener("pointercancel", handlePointerEnd);
    };
  }, [elements, activeElementId, saveToHistory]);
}

/**
 * Main Application Component
 *
 * This component manages the global state of the application and coordinates
 * the canvas, UI panels, and history.
 */
function App() {
  // useRef: Creates a mutable reference that persists across renders.
  // We use it here to get direct access to DOM elements (canvas, inputs).
  const canvasRef = useRef(null);
  const imagesInputRef = useRef(null);
  const loadProjectInputRef = useRef(null);

  // useSignal: Creates reactive state variables.
  // Unlike React's useState, updating a signal doesn't trigger a re-render of the whole component,
  // only the specific parts that read the signal.
  const backgroundColor = useSignal("#111827");
  const elements = useSignal(/** @type {EditorElement[]} */ ([]));
  const activeElementId = useSignal(null);
  const canvasSize = useSignal({ width: 1080, height: 1080 });

  // useComputed: Creates a derived signal.
  // It automatically updates whenever the signals it depends on (elements, activeElementId) change.
  // This is efficient because it caches the result.
  const activeElement = useComputed(
    () => elements.value.find((el) => el.id === activeElementId.value) || null,
  );

  // Custom Hook: useHistory
  // Manages undo/redo functionality. We pass the signals it needs to track.
  const { undo, redo, saveToHistory, undoStack, redoStack } = useHistory(
    elements,
    activeElementId,
  );

  // Custom Hook: useCanvasInteraction
  // Handles all mouse/touch interactions with the canvas.
  useCanvasInteraction(canvasRef, elements, activeElementId, saveToHistory);

  // useSignalEffect: Runs a side effect whenever the signals accessed inside it change.
  // This is our main render loop for the canvas. It runs automatically when
  // elements.value, activeElementId.value, etc., change.
  useSignalEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: false });
    const width = canvasSize.value.width;
    const height = canvasSize.value.height;
    const bg = backgroundColor.value;
    const els = elements.value;
    const active = activeElement.value;
    setHasProjectContent(els.length > 0);

    // Ensure canvas dimensions match state
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    /**
     * CANVAS STATE MANAGEMENT: Main Render Loop save/restore
     *
     * Why save at the start of render:
     * - Establishes a clean baseline state for the entire render cycle
     * - Protects against any residual state from previous renders or external code
     * - Even though we expect clean state, this is defensive programming
     *
     * What happens during this render cycle:
     * 1. We set fillStyle for background
     * 2. We call drawElement() for each element (each has its own save/restore)
     * 3. We call drawActiveOverlay() (also has its own save/restore)
     * 4. Each sub-function's save/restore is NESTED within this outer save/restore
     *
     * Nested save/restore visualization:
     * ctx.save()                    // Main render - Level 0
     *   ctx.fillStyle = bg;         // Set background color
     *   forEach element:
     *     ctx.save()                // drawElement - Level 1
     *       ctx.translate(...)      // Element transformations
     *       ctx.rotate(...)
     *       [draw element]
     *     ctx.restore()             // Back to Level 0
     *   if active:
     *     ctx.save()                // drawActiveOverlay - Level 1
     *       ctx.translate(...)      // Overlay transformations
     *       ctx.rotate(...)
     *       [draw overlay]
     *     ctx.restore()             // Back to Level 0
     * ctx.restore()                 // Back to initial state
     *
     * Why this pattern is critical:
     * - Each layer of save/restore creates an isolated context
     * - Child functions can't accidentally corrupt parent state
     * - Parent functions can't accidentally affect children
     * - Makes code modular and prevents spooky action-at-a-distance bugs
     */
    ctx.save();
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);

    els.forEach((element) => {
      drawElement(ctx, element);
    });

    if (active) {
      drawActiveOverlay(ctx, active);
    }

    /**
     * CANVAS STATE MANAGEMENT: Main Render Loop restore()
     *
     * Final cleanup:
     * - Restores canvas to the exact state it was before this render cycle
     * - Ensures clean slate for next render (which happens on every signal change)
     * - Protects against accumulated state corruption over multiple renders
     *
     * Performance consideration:
     * - This render loop runs via useSignalEffect - it's reactive and frequent
     * - Could run 60+ times per second during animations/interactions
     * - save/restore is VERY fast (native browser code), so no performance concern
     * - Much faster than debugging mysterious coordinate system bugs!
     *
     * State stack guarantee:
     * - By this point, all nested save/restore pairs from drawElement and
     *   drawActiveOverlay have been balanced (popped)
     * - This final restore() only pops the outer save() from line ~949
     * - If we forgot a restore() in a sub-function, the stack would be corrupted here
     */
    ctx.restore();
  });

  const handleAddImages = () => {
    imagesInputRef.current?.click();
  };

  const handleFilesChange = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith("image/"),
    );
    for (const file of files) {
      const dataUrl = await fileToDataUrl(file);
      await addImageElement(dataUrl, file.name);
    }
    e.target.value = "";
  };

  // useEffect: Handles side effects that aren't signal-based.
  // Here we set up global event listeners for 'paste' and 'keydown'.
  // The dependency array [undo, redo] ensures the effect re-runs if those functions change.
  useEffect(() => {
    const handlePaste = async (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            const dataUrl = await fileToDataUrl(blob);
            await addImageElement(dataUrl, `pasted-${Date.now()}.png`);
          }
        }
      }
    };

    const handleKeyDown = (event) => {
      if (
        event.key === "z" &&
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey
      ) {
        event.preventDefault();
        undo();
      }
      if (
        event.key === "z" &&
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey
      ) {
        event.preventDefault();
        redo();
      }
    };

    document.addEventListener("paste", handlePaste);
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup function: Removes listeners when the component unmounts or re-runs.
    return () => {
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [undo, redo]);

  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const addImageElement = async (dataUrl, name) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = dataUrl;
    });

    const canvas = canvasRef.current;
    const maxInitial = Math.min(canvas.width, canvas.height) * 0.7;
    let width = image.naturalWidth;
    let height = image.naturalHeight;
    if (width > height && width > maxInitial) {
      const ratio = maxInitial / width;
      width *= ratio;
      height *= ratio;
    } else if (height >= width && height > maxInitial) {
      const ratio = maxInitial / height;
      width *= ratio;
      height *= ratio;
    }

    /** @type {ImageElement} */
    const element = {
      id: createId(),
      type: "image",
      name: name || "Image",
      image,
      dataUrl,
      width,
      height,
      x: canvas.width / 2,
      y: canvas.height / 2,
      scale: 1,
      rotation: 0,
      opacity: 1,
      crop: { zoom: 1, offsetX: 0, offsetY: 0 },
    };

    saveToHistory();
    // batch: Groups multiple signal updates into a single notification.
    // This prevents unnecessary intermediate re-renders or effect runs.
    batch(() => {
      elements.value = [...elements.value, element];
      activeElementId.value = element.id;
    });
  };

  const addTextElement = (style) => {
    const canvas = canvasRef.current;
    const isMeme = style === "meme";
    /** @type {TextElement} */
    const element = {
      id: createId(),
      type: "text",
      name: isMeme ? "Meme Text" : "Text",
      text: isMeme ? "TOP TEXT\nBOTTOM TEXT" : "Your text here",
      fontFamily: isMeme ? "meme" : "regular",
      fontSize: isMeme ? 120 : 72,
      lineHeight: isMeme ? 1 : 1.15,
      color: isMeme ? "#ffffff" : "#f8fafc",
      strokeColor: isMeme ? "#000000" : "#000000",
      strokeWidth: isMeme ? 8 : 0,
      textAlign: "center",
      x: canvas.width / 2,
      y: canvas.height / 2,
      rotation: 0,
      opacity: 1,
    };

    saveToHistory();
    batch(() => {
      elements.value = [...elements.value, element];
      activeElementId.value = element.id;
    });
  };

  const removeElement = (id) => {
    saveToHistory();
    batch(() => {
      elements.value = elements.value.filter((el) => el.id !== id);
      if (activeElementId.value === id) {
        activeElementId.value = null;
      }
    });
  };

  const moveLayer = (id, direction) => {
    saveToHistory();
    const prev = elements.value;
    const index = prev.findIndex((el) => el.id === id);
    if (index === -1) return;

    const newElements = [...prev];
    if (direction === "up" && index < newElements.length - 1) {
      [newElements[index], newElements[index + 1]] = [
        newElements[index + 1],
        newElements[index],
      ];
    } else if (direction === "down" && index > 0) {
      [newElements[index], newElements[index - 1]] = [
        newElements[index - 1],
        newElements[index],
      ];
    }
    elements.value = newElements;
  };

  const updateElement = (id, updates) => {
    elements.value = elements.value.map((el) =>
      el.id === id ? { ...el, ...updates } : el,
    );
  };

  const exportImage = () => {
    const canvas = canvasRef.current;
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `image-editor-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      "image/png",
      0.95,
    );
  };

  const copyToClipboard = async () => {
    try {
      const canvas = canvasRef.current;
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/png", 0.95);
      });
      if (!blob) return;

      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      console.log("Image copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      alert(
        "Failed to copy image to clipboard. This feature requires a secure context (HTTPS).",
      );
    }
  };

  const saveProject = () => {
    const canvas = canvasRef.current;
    const data = {
      version: 1,
      canvas: {
        width: canvas.width,
        height: canvas.height,
        backgroundColor: backgroundColor.value,
      },
      elements: elements.value.map((element) => {
        if (element.type === "image") {
          return {
            type: "image",
            id: element.id,
            name: element.name,
            dataUrl: element.dataUrl,
            width: element.width,
            height: element.height,
            x: element.x,
            y: element.y,
            scale: element.scale,
            rotation: element.rotation,
            opacity: element.opacity,
            crop: element.crop,
          };
        }
        return {
          type: "text",
          id: element.id,
          name: element.name,
          text: element.text,
          fontFamily: element.fontFamily,
          fontSize: element.fontSize,
          lineHeight: element.lineHeight,
          color: element.color,
          strokeColor: element.strokeColor,
          strokeWidth: element.strokeWidth,
          textAlign: element.textAlign,
          x: element.x,
          y: element.y,
          rotation: element.rotation,
          opacity: element.opacity,
        };
      }),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `image-editor-project-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const loadProject = () => {
    loadProjectInputRef.current?.click();
  };

  const handleLoadProject = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      const json = atob(dataUrl.split(",")[1]);
      const data = JSON.parse(json);
      await loadProjectFromData(data);
    } catch (error) {
      console.error(error);
      alert("Could not load project file.");
    }
    e.target.value = "";
  };

  const loadProjectFromData = async (data) => {
    const canvas = canvasRef.current;
    if (!data || !data.canvas || !Array.isArray(data.elements)) {
      throw new Error("Invalid project file.");
    }

    batch(() => {
      canvasSize.value = {
        width: data.canvas.width,
        height: data.canvas.height,
      };
      backgroundColor.value = data.canvas.backgroundColor || "#111827";
      elements.value = [];
      activeElementId.value = null;
    });

    for (const element of data.elements) {
      if (element.type === "image") {
        await addImageElement(element.dataUrl, element.name);
        // Update the last added element with specific props from file
        const currentElements = elements.peek();
        const last = currentElements[currentElements.length - 1];
        if (last && last.type === "image") {
          /** @type {ImageElement} */
          const updated = {
            ...last,
            x: element.x ?? last.x,
            y: element.y ?? last.y,
            scale: element.scale ?? last.scale,
            rotation: element.rotation ?? last.rotation,
            opacity: element.opacity ?? last.opacity,
            crop: { ...last.crop, ...(element.crop || {}) },
          };
          elements.value = [...currentElements.slice(0, -1), updated];
        }
      } else if (element.type === "text") {
        /** @type {TextElement} */
        const newElement = {
          id: createId(),
          type: "text",
          name: element.name || "Text",
          text: element.text || "",
          fontFamily: element.fontFamily === "meme" ? "meme" : "regular",
          fontSize: clamp(
            element.fontSize || 72,
            CONFIG.minFontSize,
            CONFIG.maxFontSize,
          ),
          lineHeight: clamp(element.lineHeight || 1.1, 0.6, 3),
          color: element.color || "#ffffff",
          strokeColor: element.strokeColor || "#000000",
          strokeWidth: clamp(element.strokeWidth ?? 0, 0, 48),
          textAlign: element.textAlign || "center",
          x: element.x ?? canvas.width / 2,
          y: element.y ?? canvas.height / 2,
          rotation: element.rotation ?? 0,
          opacity: element.opacity ?? 1,
        };
        elements.value = [...elements.peek(), newElement];
      }
    }

    setTimeout(() => {
      const currentElements = elements.peek();
      if (currentElements.length > 0) {
        activeElementId.value = currentElements[currentElements.length - 1].id;
      }
    }, 100);
  };

  const clearCanvas = () => {
    saveToHistory();
    batch(() => {
      elements.value = [];
      activeElementId.value = null;
    });
  };

  const handleCanvasSizeChange = (e) => {
    const [widthStr, heightStr] = e.target.value.split("x");
    const width = Number(widthStr);
    const height = Number(heightStr);
    if (!Number.isFinite(width) || !Number.isFinite(height)) return;

    batch(() => {
      canvasSize.value = { width, height };
      elements.value = elements.value.map((el) => ({
        ...el,
        x: clamp(el.x, 0, width),
        y: clamp(el.y, 0, height),
      }));
    });
  };

  const handleBackgroundColorChange = (value) => {
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)) return;
    backgroundColor.value = value;
  };

  return html`
    <main class="min-h-screen pb-16">
      <header class="px-4 pt-6 pb-3 max-w-5xl mx-auto">
        <h1 class="text-2xl font-semibold tracking-tight text-white">
          Layered Image Editor
        </h1>
      </header>

      <section class="px-4 pb-10 max-w-6xl mx-auto">
        <div class="grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          <div class="space-y-4">
            <${CanvasPanel}
              canvasRef=${canvasRef}
              canvasSize=${canvasSize.value}
              activeElement=${activeElement.value}
              onExport=${exportImage}
              onCopyToClipboard=${copyToClipboard}
              onSave=${saveProject}
              onLoad=${loadProject}
              onClear=${clearCanvas}
              onUndo=${undo}
              onRedo=${redo}
              canUndo=${undoStack.value.length > 0}
              canRedo=${redoStack.value.length > 0}
            />
            <${LayersPanel}
              elements=${elements.value}
              activeElementId=${activeElementId.value}
              onSelectElement=${(id) => (activeElementId.value = id)}
              onMoveLayer=${moveLayer}
              onRemoveElement=${removeElement}
            />
          </div>

          <aside class="space-y-4">
            <${AddContentPanel}
              onAddImages=${handleAddImages}
              onAddText=${() => addTextElement("regular")}
              onAddMemeText=${() => addTextElement("meme")}
              imagesInputRef=${imagesInputRef}
              onFilesChange=${handleFilesChange}
            />
            <${CanvasSettingsPanel}
              canvasSize=${canvasSize.value}
              backgroundColor=${backgroundColor.value}
              onCanvasSizeChange=${handleCanvasSizeChange}
              onBackgroundColorChange=${handleBackgroundColorChange}
            />
            <${ActiveLayerPanel}
              activeElement=${activeElement.value}
              onUpdateElement=${updateElement}
              onDuplicate=${() => {
                const el = activeElement.value;
                if (!el) return;
                if (el.type === "image") {
                  addImageElement(el.dataUrl, el.name + " copy").then(() => {
                    const current = elements.peek();
                    const last = current[current.length - 1];
                    if (last) {
                      const updated = {
                        ...last,
                        x: el.x + 40,
                        y: el.y + 40,
                        crop: { ...el.crop },
                      };
                      elements.value = [...current.slice(0, -1), updated];
                    }
                  });
                } else {
                  const clone = {
                    ...el,
                    id: createId(),
                    name: `${el.name || "Text"} copy`,
                    x: el.x + 40,
                    y: el.y + 40,
                  };
                  saveToHistory();
                  batch(() => {
                    elements.value = [...elements.value, clone];
                    activeElementId.value = clone.id;
                  });
                }
              }}
              onDelete=${() =>
                activeElement.value && removeElement(activeElement.value.id)}
              onBringForward=${() =>
                activeElement.value && moveLayer(activeElement.value.id, "up")}
              onSendBackward=${() =>
                activeElement.value &&
                moveLayer(activeElement.value.id, "down")}
            />
          </aside>
        </div>
      </section>

      <input
        ref=${loadProjectInputRef}
        type="file"
        accept="application/json"
        class="hidden"
        onChange=${handleLoadProject}
      />
    </main>
  `;
}

/**
 * CanvasPanel Component
 * Displays the main canvas area and the top toolbar.
 * Handles the rendering surface and global actions like Export, Save, and Undo/Redo.
 *
 * @param {CanvasPanelProps} props
 */
function CanvasPanel({
  canvasRef,
  canvasSize,
  activeElement,
  onExport,
  onCopyToClipboard,
  onSave,
  onLoad,
  onClear,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}) {
  return html`
    <div
      class="rounded-xl border border-slate-800/70 bg-slate-900/60 p-4 shadow-lg flex flex-col gap-3 lg:gap-4"
    >
      <div
        class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h2 class="text-xl font-semibold text-white">Canvas</h2>
          <p class="text-xs text-slate-400">
            Pinch with two fingers or drag handles to resize and rotate layers.
          </p>
        </div>
        <div class="flex flex-wrap gap-2 text-xs">
          <button
            onClick=${onExport}
            class="rounded-lg bg-emerald-500 px-3 py-1.5 font-semibold uppercase tracking-wide text-emerald-950 hover:bg-emerald-400 active:translate-y-px transition"
          >
            Export PNG
          </button>
          <button
            onClick=${onCopyToClipboard}
            class="rounded-lg bg-blue-500 px-3 py-1.5 font-semibold uppercase tracking-wide text-blue-950 hover:bg-blue-400 active:translate-y-px transition"
          >
            Copy to Clipboard
          </button>
          <button
            onClick=${onSave}
            class="rounded-lg border border-slate-600 px-3 py-1.5 font-semibold uppercase tracking-wide text-slate-100 hover:bg-slate-800 active:translate-y-px transition"
          >
            Save Project
          </button>
          <button
            onClick=${onLoad}
            class="rounded-lg border border-slate-600 px-3 py-1.5 font-semibold uppercase tracking-wide text-slate-100 hover:bg-slate-800 active:translate-y-px transition"
          >
            Load Project
          </button>
        </div>
      </div>

      <div
        class="relative rounded-xl border border-slate-800 bg-slate-950/70 shadow-inner overflow-hidden"
      >
        <canvas
          ref=${canvasRef}
          width=${canvasSize.width}
          height=${canvasSize.height}
          class="block w-full h-auto touch-none select-none bg-slate-900/80"
          style=${{ aspectRatio: `${canvasSize.width}/${canvasSize.height}` }}
        />
      </div>

      <div
        class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs text-slate-400"
      >
        <p class="leading-snug">
          Tip: Pinch to resize/rotate on touch. Drag white handles on desktop.
        </p>
        <div class="flex flex-wrap gap-2">
          <button
            onClick=${onUndo}
            disabled=${!canUndo}
            class="self-start rounded-md border border-slate-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-800 active:translate-y-px transition"
          >
            ← Undo
          </button>
          <button
            onClick=${onRedo}
            disabled=${!canRedo}
            class="self-start rounded-md border border-slate-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-800 active:translate-y-px transition"
          >
            Redo →
          </button>
          <button
            onClick=${onClear}
            class="self-start rounded-md border border-rose-500/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-rose-200 hover:bg-rose-500/10 active:translate-y-px transition"
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * LayersPanel Component
 * Lists all elements on the canvas in a scrollable list.
 * Allows selecting, reordering (z-index), and deleting layers.
 *
 * @param {LayersPanelProps} props
 */
function LayersPanel({
  elements,
  activeElementId,
  onSelectElement,
  onMoveLayer,
  onRemoveElement,
}) {
  let layersContent;

  if (elements.length === 0) {
    layersContent = html`<p class="text-xs text-slate-500">
      Add an image or text to begin.
    </p>`;
  } else {
    // We reverse the elements for display so the top-most layer appears at the top of the list
    const reversedElements = [...elements].reverse();
    layersContent = html`
      <ul class="space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
        ${reversedElements.map(
          (element) => html`
            <${LayerItem}
              key=${element.id}
              element=${element}
              isActive=${element.id === activeElementId}
              onSelect=${() => onSelectElement(element.id)}
              onMoveUp=${() => onMoveLayer(element.id, "up")}
              onMoveDown=${() => onMoveLayer(element.id, "down")}
              onDelete=${() => onRemoveElement(element.id)}
            />
          `,
        )}
      </ul>
    `;
  }

  return html`
    <div
      class="rounded-xl border border-slate-800/70 bg-slate-900/60 p-4 shadow-lg space-y-3"
    >
      <h2 class="text-xl font-semibold text-white">Layers</h2>
      <p class="text-xs text-slate-400">
        Tap a layer to edit it. Top-most items render first.
      </p>
      ${layersContent}
    </div>
  `;
}

/**
 * LayerItem Component
 * Represents a single row in the layers panel.
 *
 * @param {LayerItemProps} props
 */
function LayerItem({
  element,
  isActive,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDelete,
}) {
  let title;
  if (element.type === "image") {
    title = element.name || "Image";
  } else {
    if (element.fontFamily === "meme") {
      title = element.name || "Meme Text";
    } else {
      title = element.name || "Text";
    }
  }

  let itemClasses;
  if (isActive) {
    itemClasses =
      "flex items-center justify-between gap-2.5 rounded-lg border px-3 py-2 shadow border-blue-500 bg-blue-500/10";
  } else {
    itemClasses =
      "flex items-center justify-between gap-2.5 rounded-lg border px-3 py-2 shadow border-slate-700 bg-slate-950/60";
  }

  return html`
    <li class=${itemClasses}>
      <button
        type="button"
        onClick=${onSelect}
        class="flex-1 text-left text-sm font-medium text-slate-100 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 truncate"
      >
        ${title}
      </button>
      <div class="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick=${onMoveUp}
          class="rounded-md border border-slate-700 bg-slate-800/70 px-1.5 py-1 text-xs text-slate-200 hover:bg-slate-700 transition"
          aria-label="Move layer up"
        >
          ↑
        </button>
        <button
          type="button"
          onClick=${onMoveDown}
          class="rounded-md border border-slate-700 bg-slate-800/70 px-1.5 py-1 text-xs text-slate-200 hover:bg-slate-700 transition"
          aria-label="Move layer down"
        >
          ↓
        </button>
        <button
          type="button"
          onClick=${onDelete}
          class="rounded-md border border-rose-500/70 bg-rose-500/10 px-1.5 py-1 text-xs text-rose-200 hover:bg-rose-500/20 transition"
          aria-label="Remove layer"
        >
          ✕
        </button>
      </div>
    </li>
  `;
}

/**
 * AddContentPanel Component
 * Sidebar panel containing buttons to add new content (Images, Text, Meme Text).
 *
 * @param {AddContentPanelProps} props
 */
function AddContentPanel({
  onAddImages,
  onAddText,
  onAddMemeText,
  imagesInputRef,
  onFilesChange,
}) {
  return html`
    <div
      class="rounded-xl border border-slate-800/70 bg-slate-900/60 p-4 shadow-lg space-y-3"
    >
      <h2 class="text-xl font-semibold text-white">Add Content</h2>
      <div class="flex flex-col gap-2.5">
        <button
          onClick=${onAddImages}
          class="w-full rounded-lg border border-blue-500/80 bg-blue-500/10 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-blue-200 hover:bg-blue-500/20 active:translate-y-px transition"
        >
          Add Images
        </button>
        <input
          type="file"
          accept="image/*"
          multiple
          class="hidden"
          ref=${imagesInputRef}
          onChange=${onFilesChange}
        />
        <p class="text-xs text-slate-400 -mt-0.5">
          Tip: You can also paste images from your clipboard (Cmd/Ctrl+V)
        </p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <button
            onClick=${onAddText}
            class="rounded-lg border border-slate-700 bg-slate-800/70 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-slate-100 hover:bg-slate-700/70 active:translate-y-px transition"
          >
            Add Text
          </button>
          <button
            onClick=${onAddMemeText}
            class="rounded-lg border border-slate-700 bg-slate-800/70 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-yellow-200 hover:bg-slate-700/70 active:translate-y-px transition"
          >
            Add Meme Text
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * CanvasSettingsPanel Component
 * Sidebar panel for global canvas configuration.
 *
 * @param {CanvasSettingsPanelProps} props
 */
function CanvasSettingsPanel({
  canvasSize,
  backgroundColor,
  onCanvasSizeChange,
  onBackgroundColorChange,
}) {
  const currentSize = `${canvasSize.width}x${canvasSize.height}`;

  return html`
    <div
      class="rounded-xl border border-slate-800/70 bg-slate-900/60 p-4 shadow-lg space-y-3"
    >
      <h2 class="text-xl font-semibold text-white">Canvas Settings</h2>
      <div class="space-y-2.5">
        <label class="block text-xs text-slate-300">
          <span
            class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
            >Size</span
          >
          <select
            value=${currentSize}
            onChange=${onCanvasSizeChange}
            class="w-full rounded-md border border-slate-700 bg-slate-950/70 px-2.5 py-2 text-sm text-slate-100 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="1080x1080">Square – 1080 × 1080</option>
            <option value="1920x1080">Landscape – 1920 × 1080</option>
            <option value="1080x1920">Portrait – 1080 × 1920</option>
            <option value="2048x1152">YouTube Banner – 2048 × 1152</option>
          </select>
        </label>
        <label class="block text-xs text-slate-300">
          <span
            class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
            >Background</span
          >
          <div class="flex items-center gap-2.5">
            <input
              type="color"
              value=${backgroundColor}
              onChange=${(e) => onBackgroundColorChange(e.target.value)}
              class="h-9 w-12 rounded border border-slate-700 bg-slate-950/70"
            />
            <input
              type="text"
              value=${backgroundColor}
              onChange=${(e) => onBackgroundColorChange(e.target.value)}
              placeholder="#111827"
              class="flex-1 rounded-md border border-slate-700 bg-slate-950/70 px-2.5 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </label>
      </div>
    </div>
  `;
}

/**
 * ActiveLayerPanel Component
 * Context-aware sidebar panel that shows controls for the selected element.
 * Switches between ImageControls and TextControls based on element type.
 *
 * @param {ActiveLayerPanelProps} props
 */
function ActiveLayerPanel({
  activeElement,
  onUpdateElement,
  onDuplicate,
  onDelete,
  onBringForward,
  onSendBackward,
}) {
  if (!activeElement) {
    return html`
      <div
        class="rounded-xl border border-slate-800/70 bg-slate-900/60 p-4 shadow-lg space-y-3"
      >
        <h2 class="text-xl font-semibold text-white">Active Layer</h2>
        <p class="text-xs text-slate-500">
          Select a layer to see editing controls.
        </p>
      </div>
    `;
  }

  const controlsContent =
    activeElement.type === "image"
      ? html`<${ImageControls}
          element=${activeElement}
          onUpdateElement=${onUpdateElement}
        />`
      : html`<${TextControls}
          element=${activeElement}
          onUpdateElement=${onUpdateElement}
        />`;

  return html`
    <div
      class="rounded-xl border border-slate-800/70 bg-slate-900/60 p-4 shadow-lg space-y-3"
    >
      <h2 class="text-xl font-semibold text-white">Active Layer</h2>

      <div class="space-y-3">
        <div class="space-y-2.5">
          <label class="block text-xs text-slate-300">
            <span
              class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
              >Rotation</span
            >
            <input
              type="range"
              min="-180"
              max="180"
              step="1"
              value=${Math.round(radiansToDegrees(activeElement.rotation))}
              onInput=${(e) =>
                onUpdateElement(activeElement.id, {
                  rotation: degreesToRadians(Number(e.target.value)),
                })}
              class="w-full accent-blue-400"
            />
            <span class="mt-1 block text-[0.7rem] text-slate-400"
              >${Math.round(radiansToDegrees(activeElement.rotation))}°</span
            >
          </label>
          <label class="block text-xs text-slate-300">
            <span
              class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
              >Opacity</span
            >
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value=${activeElement.opacity ?? 1}
              onInput=${(e) =>
                onUpdateElement(activeElement.id, {
                  opacity: Number(e.target.value),
                })}
              class="w-full accent-blue-400"
            />
            <span class="mt-1 block text-[0.7rem] text-slate-400"
              >${Math.round((activeElement.opacity ?? 1) * 100)}%</span
            >
          </label>
        </div>

        ${controlsContent}

        <div class="flex flex-wrap gap-2 text-xs">
          <button
            onClick=${onDuplicate}
            class="rounded-md border border-slate-600 px-3 py-1.5 font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-800 active:translate-y-px transition"
          >
            Duplicate
          </button>
          <button
            onClick=${onDelete}
            class="rounded-md border border-rose-500/70 px-3 py-1.5 font-semibold uppercase tracking-wide text-rose-200 hover:bg-rose-500/10 active:translate-y-px transition"
          >
            Delete
          </button>
          <button
            onClick=${onBringForward}
            class="rounded-md border border-slate-600 px-3 py-1.5 font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-800 active:translate-y-px transition"
          >
            Bring Forward
          </button>
          <button
            onClick=${onSendBackward}
            class="rounded-md border border-slate-600 px-3 py-1.5 font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-800 active:translate-y-px transition"
          >
            Send Backward
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * ImageControls Component
 * Specific property controls for Image elements (Scale, Crop Zoom/Pan).
 *
 * @param {ImageControlsProps} props
 */
function ImageControls({ element, onUpdateElement }) {
  return html`
    <div class="space-y-2.5">
      <label class="block text-xs text-slate-300">
        <span
          class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
          >Size</span
        >
        <input
          type="range"
          min="0.2"
          max="5"
          step="0.01"
          value=${element.scale}
          onInput=${(e) =>
            onUpdateElement(element.id, {
              scale: clamp(
                Number(e.target.value),
                CONFIG.minScale,
                CONFIG.maxScale,
              ),
            })}
          class="w-full accent-blue-400"
        />
        <span class="mt-1 block text-[0.7rem] text-slate-400"
          >${Math.round(element.scale * 100)}%</span
        >
      </label>
      <details class="rounded-lg border border-slate-700 bg-slate-950/60 p-3">
        <summary class="cursor-pointer text-sm font-semibold text-slate-200">
          Crop
        </summary>
        <div class="mt-3 space-y-2.5">
          <label class="block text-xs text-slate-300">
            <span
              class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
              >Zoom</span
            >
            <input
              type="range"
              min="1"
              max="4"
              step="0.01"
              value=${element.crop.zoom}
              onInput=${(e) =>
                onUpdateElement(element.id, {
                  crop: {
                    ...element.crop,
                    zoom: clamp(Number(e.target.value), 1, 6),
                  },
                })}
              class="w-full accent-blue-400"
            />
            <span class="mt-1 block text-[0.7rem] text-slate-400"
              >${Number(element.crop.zoom).toFixed(2)}×</span
            >
          </label>
          <label class="block text-xs text-slate-300">
            <span
              class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
              >Horizontal</span
            >
            <input
              type="range"
              min="-1"
              max="1"
              step="0.01"
              value=${element.crop.offsetX}
              onInput=${(e) =>
                onUpdateElement(element.id, {
                  crop: {
                    ...element.crop,
                    offsetX: clamp(Number(e.target.value), -1, 1),
                  },
                })}
              class="w-full accent-blue-400"
            />
          </label>
          <label class="block text-xs text-slate-300">
            <span
              class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
              >Vertical</span
            >
            <input
              type="range"
              min="-1"
              max="1"
              step="0.01"
              value=${element.crop.offsetY}
              onInput=${(e) =>
                onUpdateElement(element.id, {
                  crop: {
                    ...element.crop,
                    offsetY: clamp(Number(e.target.value), -1, 1),
                  },
                })}
              class="w-full accent-blue-400"
            />
          </label>
          <button
            onClick=${() =>
              onUpdateElement(element.id, {
                crop: { zoom: 1, offsetX: 0, offsetY: 0 },
              })}
            class="w-full rounded-md border border-slate-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-800 active:translate-y-px transition"
          >
            Reset Crop
          </button>
        </div>
      </details>
    </div>
  `;
}

/**
 * TextControls Component
 * Specific property controls for Text elements (Content, Font, Color, Stroke).
 *
 * @param {TextControlsProps} props
 */
function TextControls({ element, onUpdateElement }) {
  return html`
    <div class="space-y-3">
      <label class="block text-xs text-slate-300">
        <span
          class="mb-1 block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-400"
          >Text</span
        >
        <textarea
          rows="3"
          value=${element.text}
          onInput=${(e) =>
            onUpdateElement(element.id, { text: e.target.value })}
          placeholder="Enter text"
          class="w-full rounded-md border border-slate-700 bg-slate-950/70 px-2.5 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </label>
      <div class="grid gap-2.5 sm:grid-cols-2">
        <label class="block text-xs text-slate-300">
          <span
            class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400"
            >Style</span
          >
          <select
            value=${element.fontFamily}
            onChange=${(e) => {
              const isMeme = e.target.value === "meme";
              /** @type {Partial<TextElement>} */
              const updates = {};
              if (isMeme) {
                updates.fontFamily = "meme";
              } else {
                updates.fontFamily = "regular";
              }
              if (isMeme && element.strokeWidth < 6) {
                updates.strokeWidth = 8;
              }
              onUpdateElement(element.id, updates);
            }}
            class="w-full rounded-md border border-slate-700 bg-slate-950/70 px-2.5 py-2 text-sm text-slate-100 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="regular">Regular</option>
            <option value="meme">Meme</option>
          </select>
        </label>
        <label class="block text-xs text-slate-300">
          <span
            class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400"
            >Alignment</span
          >
          <select
            value=${element.textAlign}
            onChange=${(e) =>
              onUpdateElement(element.id, { textAlign: e.target.value })}
            class="w-full rounded-md border border-slate-700 bg-slate-950/70 px-2.5 py-2 text-sm text-slate-100 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="center">Center</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </label>
      </div>
      <label class="block text-xs text-slate-300">
        <span
          class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400"
          >Font Size</span
        >
        <input
          type="range"
          min="12"
          max="220"
          step="1"
          value=${element.fontSize}
          onInput=${(e) =>
            onUpdateElement(element.id, {
              fontSize: clamp(
                Number(e.target.value),
                CONFIG.minFontSize,
                CONFIG.maxFontSize,
              ),
            })}
          class="w-full accent-blue-400"
        />
        <span class="mt-1 block text-xs text-slate-400"
          >${Math.round(element.fontSize)} px</span
        >
      </label>
      <label class="block text-xs text-slate-300">
        <span
          class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400"
          >Line Height</span
        >
        <input
          type="range"
          min="0.8"
          max="2"
          step="0.05"
          value=${element.lineHeight}
          onInput=${(e) =>
            onUpdateElement(element.id, {
              lineHeight: clamp(Number(e.target.value), 0.6, 3),
            })}
          class="w-full accent-blue-400"
        />
        <span class="mt-1 block text-xs text-slate-400"
          >${element.lineHeight.toFixed(2)}</span
        >
      </label>
      <div class="grid gap-2.5 sm:grid-cols-2">
        <label class="block text-xs text-slate-300">
          <span
            class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400"
            >Fill</span
          >
          <input
            type="color"
            value=${element.color}
            onChange=${(e) =>
              onUpdateElement(element.id, { color: e.target.value })}
            class="h-9 w-full rounded border border-slate-700 bg-slate-950/70"
          />
        </label>
        <label class="block text-xs text-slate-300">
          <span
            class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400"
            >Outline</span
          >
          <input
            type="color"
            value=${element.strokeColor}
            onChange=${(e) =>
              onUpdateElement(element.id, { strokeColor: e.target.value })}
            class="h-9 w-full rounded border border-slate-700 bg-slate-950/70"
          />
        </label>
      </div>
      <label class="block text-xs text-slate-300">
        <span
          class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400"
          >Outline width</span
        >
        <input
          type="range"
          min="0"
          max="24"
          step="1"
          value=${element.strokeWidth}
          onInput=${(e) =>
            onUpdateElement(element.id, {
              strokeWidth: clamp(Number(e.target.value), 0, 48),
            })}
          class="w-full accent-blue-400"
        />
        <span class="mt-1 block text-xs text-slate-400"
          >${Math.round(element.strokeWidth)} px</span
        >
      </label>
    </div>
  `;
}

// Render app
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

render(html`<${App} />`, rootElement);
void setupServiceWorkerUpdateListener();

function isHomeRoute() {
  const pathname = window.location.pathname;
  return pathname === "/" || pathname === "/index.html";
}

function canActivateWaitingWorker() {
  if (!isHomeRoute()) {
    return false;
  }

  if (document.visibilityState !== "visible") {
    return false;
  }

  if (serviceWorkerUpdateState.hasProjectContent) {
    return false;
  }

  if (serviceWorkerUpdateState.activePointerCount > 0) {
    return false;
  }

  const idleTime = Date.now() - serviceWorkerUpdateState.lastInteractionAt;
  return idleTime > 30000;
}

/**
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
async function getServiceWorkerRegistration() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return null;
    }

    return registration;
  } catch (error) {
    console.error("[App] Failed to get service worker registration:", error);
  }

  return null;
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<void>}
 */
async function tryActivateWaitingWorker(registration) {
  if (!registration.waiting) {
    return;
  }

  if (!canActivateWaitingWorker()) {
    return;
  }

  const waitingWorker = registration.waiting;
  if (!waitingWorker) {
    return;
  }

  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<void>}
 */
async function requestServiceWorkerUpdate(registration) {
  try {
    await registration.update();
  } catch (error) {
    console.debug("[App] Service worker update check failed:", error);
  }
}

async function setupServiceWorkerUpdateListener() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    await navigator.serviceWorker.ready;
  } catch (error) {
    console.error("[App] Service worker not ready:", error);
    return;
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return;
  }

  async function runSafeActivationCheck() {
    const currentRegistration = await getServiceWorkerRegistration();
    if (!currentRegistration) {
      return;
    }

    await tryActivateWaitingWorker(currentRegistration);
  }

  async function runBackgroundUpdateCheck() {
    await requestServiceWorkerUpdate(registration);
    await runSafeActivationCheck();
  }

  /**
   * @param {ServiceWorker} installingWorker
   * @returns {void}
   */
  function watchInstallingWorker(installingWorker) {
    installingWorker.addEventListener("statechange", () => {
      if (installingWorker.state !== "installed") {
        return;
      }

      void runSafeActivationCheck();
    });
  }

  function watchRegistrationInstallingWorker() {
    const installingWorker = registration.installing;
    if (!installingWorker) {
      return;
    }

    watchInstallingWorker(installingWorker);
  }

  registration.addEventListener(
    "updatefound",
    watchRegistrationInstallingWorker,
  );
  watchRegistrationInstallingWorker();

  window.addEventListener("pointerdown", markAppInteraction, { passive: true });
  window.addEventListener("keydown", markAppInteraction);
  window.addEventListener("input", markAppInteraction);

  await runSafeActivationCheck();
  void runBackgroundUpdateCheck();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") {
      return;
    }

    void runSafeActivationCheck();
    void runBackgroundUpdateCheck();
  });

  window.addEventListener("pageshow", () => {
    if (document.visibilityState !== "visible") {
      return;
    }

    void runSafeActivationCheck();
    void runBackgroundUpdateCheck();
  });

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (serviceWorkerUpdateState.refreshing) {
      return;
    }

    if (!canActivateWaitingWorker()) {
      return;
    }

    serviceWorkerUpdateState.refreshing = true;
    window.location.reload();
  });
}
